import React, { useCallback } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

interface ScaleObject {
  label: string;
  size: number;
  color: string;
}

export interface ScaleSimulationProps {
  size: number;
  objects: ScaleObject[];
}

export function ScaleSimulation({ size, objects }: ScaleSimulationProps) {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, frameCount: number) => {
      ctx.clearRect(0, 0, size, size);

      if (objects.length === 0) return;

      const maxSize = Math.max(...objects.map((o) => o.size));
      const availableWidth = size / objects.length;
      const maxRadius = Math.min(availableWidth / 2.5, size / 3);

      for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        
        // Relative radius based on largest object
        const relativeRadius = (obj.size / maxSize) * maxRadius;
        
        // Positioning
        const x = availableWidth * i + availableWidth / 2;
        // Add a gentle floating animation
        const yOffset = Math.sin(frameCount * 0.02 + i) * 10;
        const y = size / 2 + yOffset;

        // Draw Sphere
        const gradient = ctx.createRadialGradient(x - relativeRadius * 0.3, y - relativeRadius * 0.3, relativeRadius * 0.1, x, y, relativeRadius);
        gradient.addColorStop(0, '#ffffff');
        gradient.addColorStop(0.3, obj.color);
        gradient.addColorStop(1, '#000000');

        ctx.beginPath();
        ctx.arc(x, y, relativeRadius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw Label
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(obj.label, x, y + maxRadius + 20 + yOffset);
      }
    },
    [size, objects]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
